/**
 * CostEstimateTab.js — Full-width cost estimate table (1920×1080 optimised).
 *
 * Features:
 *  • Editable table cells (click to edit inline)
 *  • Row checkbox → selected rows contribute to Selected Total
 *  • Image upload per item (local / URL / screenshot)
 *  • Supplier autocomplete (SupplierPicker)
 *  • Hyperlink per item
 *  • Qty × Wastage × Unit Price = Line Total
 *  • Wastage multiplier column (default 1.00)
 *  • Insert row at any position (+ button per row)
 *  • Category column sort (asc / desc)
 *  • Column manager (show/hide)
 *  • Filter bar (category / supplier / keyword)
 *  • Import CSV / XLSX
 *  • Export CSV / XLSX / PDF (jsPDF + autoTable, A4 Landscape)
 *  • Auto-save on blur / change
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Plus, Trash2, Search, Settings2, Upload, Download,
  Link as LinkIcon, Image as ImageIcon, ChevronDown, X, Loader2,
  FileText, FileSpreadsheet, FileDown, CheckSquare, Square,
  Maximize2, Minimize2, ArrowUpDown, ArrowUp, ArrowDown,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ItemImageUpload from './ItemImageUpload';
import SupplierPicker from './SupplierPicker';
import { getLOGO_URL, getBRAND_NAME } from '../../lib/brandConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_COLUMNS = [
  { key: 'selected',    label: '✓',          width: 36,  wideWidth: 44,  alwaysVisible: true },
  { key: 'seq',         label: '#',           width: 40,  wideWidth: 52,  alwaysVisible: true },
  { key: 'image',       label: 'Image',       width: 64,  wideWidth: 88 },
  { key: 'category',    label: 'Category',    width: 130, wideWidth: 190 },
  { key: 'description', label: 'Description', width: 280, wideWidth: 520, alwaysVisible: true },
  { key: 'supplier',    label: 'Supplier',    width: 180, wideWidth: 280 },
  { key: 'link',        label: 'Link',        width: 50,  wideWidth: 60 },
  { key: 'quantity',    label: 'Qty',         width: 72,  wideWidth: 96 },
  { key: 'unit',        label: 'Unit',        width: 68,  wideWidth: 88 },
  { key: 'wastage',     label: 'Wastage',     width: 76,  wideWidth: 100 },
  { key: 'unit_price',  label: 'Unit Price',  width: 108, wideWidth: 155 },
  { key: 'line_total',  label: 'Line Total',  width: 118, wideWidth: 175, alwaysVisible: true },
];

const UNITS = ['ea', 'm', 'm²', 'm³', 'kg', 't', 'l', 'hr', 'day', 'wk', 'lot', 'set', 'box', 'roll'];
const fmt = (n) => n == null ? '' : Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCurrency = (n) => `$${fmt(n)}`;

const newItem = (seq) => ({
  id: crypto.randomUUID(),
  seq, selected: true, image_url: '', category: '', description: '',
  supplier_id: '', supplier_name: '', link: '', quantity: 1,
  unit: 'ea', wastage: 1, unit_price: 0, line_total: 0,
});

const calcLineTotal = (it) =>
  Math.round(parseFloat(it.quantity || 0) * parseFloat(it.wastage || 1) * parseFloat(it.unit_price || 0) * 100) / 100;

export default function CostEstimateTab({ project }) {
  const projectId = project?.id;
  const [estimate, setEstimate] = useState(null);
  const [items, setItems] = useState([]);
  const [visibleCols, setVisibleCols] = useState(DEFAULT_COLUMNS.map(c => c.key));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editCell, setEditCell] = useState(null); // { rowId, col }
  const [imageModal, setImageModal] = useState(null); // rowId
  const [showColManager, setShowColManager] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [pendingImport, setPendingImport] = useState(null); // { all: [], unique: [], dupeCount: N }
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [sortCategory, setSortCategory] = useState(''); // '' | 'asc' | 'desc'
  const importRef = useRef();
  const saveTimer = useRef(null);

  // Escape key exits fullscreen
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // ── Load ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    axios.get(`${API}/projects/${projectId}/cost-estimate`)
      .then(res => {
        setEstimate(res.data);
        setItems(res.data.items || []);
        if (res.data.visible_columns?.length) {
          const saved = res.data.visible_columns;
          const defaultKeys = DEFAULT_COLUMNS.map(c => c.key);
          // Add any new columns from DEFAULT_COLUMNS not yet in the saved list
          const merged = [
            ...saved.filter(k => defaultKeys.includes(k)),
            ...defaultKeys.filter(k => !saved.includes(k)),
          ];
          setVisibleCols(merged);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  // ── Auto-save (debounced 1.5s) ──────────────────────────────────────────────
  const scheduleSave = useCallback((updatedItems, cols) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const payload = {
        title: estimate?.title || 'Cost Estimate',
        visible_columns: cols,
        items: updatedItems.map((it, i) => ({ ...it, seq: i })),
      };
      setSaving(true);
      axios.put(`${API}/projects/${projectId}/cost-estimate`, payload)
        .catch(err => {
          console.error('Save failed:', err);
          // Auto-save is silent on success by design; a failure must not be,
          // or edits are lost with no indication.
          toast.error(apiErrorMessage(err, 'Cost estimate auto-save failed — your last edits are not saved.'));
        })
        .finally(() => setSaving(false));
    }, 1500);
  }, [projectId, estimate]);

  const updateItems = useCallback((updater) => {
    setItems(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const recalc = next.map(it => ({ ...it, line_total: calcLineTotal(it) }));
      scheduleSave(recalc, visibleCols);
      return recalc;
    });
  }, [scheduleSave, visibleCols]);

  const updateVisibleCols = (cols) => {
    setVisibleCols(cols);
    scheduleSave(items, cols);
  };

  // ── Cell edit helpers ───────────────────────────────────────────────────────
  const setField = (id, field, value) => {
    updateItems(prev => prev.map(it => it.id === id ? { ...it, [field]: value } : it));
  };

  const addRow = () => {
    updateItems(prev => [...prev, newItem(prev.length)]);
  };

  const insertRowAfter = (id) => {
    updateItems(prev => {
      const idx = prev.findIndex(it => it.id === id);
      const inserted = newItem(idx + 1);
      const next = [...prev];
      next.splice(idx + 1, 0, inserted);
      return next;
    });
  };

  const deleteRow = (id) => {
    updateItems(prev => prev.filter(it => it.id !== id));
  };

  const toggleSelected = (id) => {
    updateItems(prev => prev.map(it => it.id === id ? { ...it, selected: !it.selected } : it));
  };

  const toggleAll = () => {
    const allSelected = items.every(it => it.selected);
    updateItems(prev => prev.map(it => ({ ...it, selected: !allSelected })));
  };

  const cycleSortCategory = () => {
    setSortCategory(s => s === '' ? 'asc' : s === 'asc' ? 'desc' : '');
  };

  // ── Filter + Sort ────────────────────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    return items.filter(it => {
      if (filterCategory && it.category !== filterCategory) return false;
      if (filterSupplier && it.supplier_name !== filterSupplier) return false;
      if (filterText) {
        const q = filterText.toLowerCase();
        return (
          it.description?.toLowerCase().includes(q) ||
          it.category?.toLowerCase().includes(q) ||
          it.supplier_name?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [items, filterText, filterCategory, filterSupplier]);

  const displayItems = useMemo(() => {
    if (!sortCategory) return filteredItems;
    return [...filteredItems].sort((a, b) => {
      const cmp = (a.category || '').localeCompare(b.category || '');
      return sortCategory === 'asc' ? cmp : -cmp;
    });
  }, [filteredItems, sortCategory]);

  const categories = useMemo(() => [...new Set(items.map(it => it.category).filter(Boolean))].sort(), [items]);
  const suppliers = useMemo(() => [...new Set(items.map(it => it.supplier_name).filter(Boolean))].sort(), [items]);

  // ── Totals ──────────────────────────────────────────────────────────────────
  const selectedItems = items.filter(it => it.selected);
  const selectedTotal = selectedItems.reduce((s, it) => s + (it.line_total || 0), 0);
  const gst = selectedTotal * 0.1;
  const totalWithGst = selectedTotal + gst;

  // ── Import ──────────────────────────────────────────────────────────────────
  const handleImportFile = async (file) => {
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/cost-estimate/import`, form);
      const imported = res.data.items || [];
      // Detect duplicates by description (case-insensitive)
      const existingDescs = new Set(items.map(it => (it.description || '').trim().toLowerCase()));
      const unique = imported.filter(it => !existingDescs.has((it.description || '').trim().toLowerCase()));
      const dupeCount = imported.length - unique.length;
      if (dupeCount > 0) {
        setPendingImport({ all: imported, unique, dupeCount });
        setShowImport(false);
      } else {
        updateItems(prev => [...prev, ...imported]);
        setShowImport(false);
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Import failed.'));
    }
  };

  // ── Export ──────────────────────────────────────────────────────────────────
  const handleExportServer = async (format) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/cost-estimate/export?format=${format}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cost_estimate.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Export failed.'));
    }
    setShowExport(false);
  };

  const handleExportPdf = useCallback(async () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pdfW = doc.internal.pageSize.getWidth();

    // Header
    doc.setFillColor(26, 26, 46);
    doc.rect(0, 0, pdfW, 22, 'F');
    doc.setFontSize(14);
    doc.setTextColor(201, 168, 76);
    doc.setFont('helvetica', 'bold');
    doc.text('Cost Estimate', 14, 14);
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text(`Project: ${project?.project_name || ''}`, 14, 20);
    doc.setFontSize(8);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-AU')}`, pdfW - 14, 20, { align: 'right' });

    autoTable(doc, {
      startY: 26,
      head: [['#', 'Category', 'Description', 'Supplier', 'Qty', 'Unit', 'Wastage', 'Unit Price', 'Line Total']],
      body: displayItems.map((it, i) => [
        i + 1,
        it.category || '',
        it.description || '',
        it.supplier_name || '',
        it.quantity ?? 0,
        it.unit || 'ea',
        it.wastage ?? 1,
        fmtCurrency(it.unit_price),
        fmtCurrency(it.line_total),
      ]),
      foot: [
        ['', '', '', '', '', '', '', 'Selected Total (excl. GST)', fmtCurrency(selectedTotal)],
        ['', '', '', '', '', '', '', 'GST (10%)', fmtCurrency(gst)],
        ['', '', '', '', '', '', '', 'Total incl. GST', fmtCurrency(totalWithGst)],
      ],
      headStyles: { fillColor: [26, 26, 46], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8, textColor: [44, 44, 44] },
      footStyles: { fillColor: [248, 247, 244], textColor: [26, 26, 46], fontStyle: 'bold', fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 8 },
        1: { cellWidth: 26 },
        2: { cellWidth: 68 },
        3: { cellWidth: 40 },
        4: { cellWidth: 12 },
        5: { cellWidth: 12 },
        6: { cellWidth: 16, halign: 'right' },
        7: { cellWidth: 24, halign: 'right' },
        8: { cellWidth: 26, halign: 'right', fontStyle: 'bold' },
      },
      didParseCell: (data) => {
        const item = displayItems[data.row.index];
        if (data.section === 'body' && item && !item.selected) {
          data.cell.styles.textColor = [156, 163, 175];
        }
        if (data.section === 'foot' && data.row.index === 2) {
          data.cell.styles.fillColor = [26, 26, 46];
          data.cell.styles.textColor = [201, 168, 76];
        }
      },
      margin: { left: 14, right: 14 },
    });

    doc.save(`cost_estimate_${project?.project_name?.replace(/\s+/g, '_') || 'export'}.pdf`);
    setShowExport(false);
  }, [displayItems, selectedTotal, gst, totalWithGst, project]);

  // ── Visible columns (use wideWidth when fullscreen) ─────────────────────────
  const visibleColDefs = DEFAULT_COLUMNS.filter(c => visibleCols.includes(c.key)).map(c => ({
    ...c,
    width: isFullscreen ? (c.wideWidth || c.width) : c.width,
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#c9a84c' }} />
      </div>
    );
  }

  // ── Fullscreen wrapper ───────────────────────────────────────────────────────
  const fsStyle = isFullscreen ? {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 200,
    background: 'var(--theme-card-bg, #f8f7f4)',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px 32px 16px',
    overflow: 'hidden',
  } : {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  };

  const SortIcon = sortCategory === 'asc' ? ArrowUp : sortCategory === 'desc' ? ArrowDown : ArrowUpDown;

  return (
    <div style={fsStyle}>

      {/* ── Fullscreen title bar ──────────────────────────────────────────────── */}
      {isFullscreen && (
        <div className="flex items-center justify-between mb-3 pb-3" style={{ borderBottom: '1px solid var(--theme-border, #e5e4df)' }}>
          <div className="flex items-center gap-3">
            <div
              className="w-7 h-7 rounded flex items-center justify-center"
              style={{ background: '#1a1a2e' }}
            >
              <span style={{ color: '#c9a84c', fontSize: 12, fontWeight: 700 }}>CE</span>
            </div>
            <div>
              <p className="font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#1a1a2e' }}>
                Cost Estimate
              </p>
              <p className="text-xs" style={{ color: '#9ca3af' }}>{project?.project_name || ''}</p>
            </div>
          </div>
          <button
            onClick={() => setIsFullscreen(false)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded font-semibold"
            style={{ background: '#1a1a2e', color: '#c9a84c' }}
          >
            <Minimize2 className="w-3.5 h-3.5" /> Exit Fullscreen
          </button>
        </div>
      )}

      {/* ── Toolbar ───────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 pb-3 flex-wrap">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: '#9ca3af' }} />
          <input
            type="text"
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            placeholder="Search…"
            className="pl-8 pr-3 py-1.5 text-sm border rounded"
            style={{ borderColor: '#e5e4df', width: 180 }}
          />
        </div>

        {/* Category filter */}
        <select
          value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}
          className="px-3 py-1.5 text-sm border rounded"
          style={{ borderColor: '#e5e4df' }}
        >
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        {/* Supplier filter */}
        <select
          value={filterSupplier}
          onChange={e => setFilterSupplier(e.target.value)}
          className="px-3 py-1.5 text-sm border rounded"
          style={{ borderColor: '#e5e4df' }}
        >
          <option value="">All Suppliers</option>
          {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {(filterText || filterCategory || filterSupplier) && (
          <button
            onClick={() => { setFilterText(''); setFilterCategory(''); setFilterSupplier(''); }}
            className="text-xs px-2 py-1.5 rounded flex items-center gap-1"
            style={{ color: '#dc2626', background: '#fee2e2' }}
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          {saving && <span className="text-xs text-[#9ca3af] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Saving…</span>}

          {/* Column manager */}
          <div className="relative">
            <button
              onClick={() => { setShowColManager(!showColManager); setShowExport(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border"
              style={{ borderColor: '#e5e4df', color: '#6b7280' }}
            >
              <Settings2 className="w-3.5 h-3.5" /> Columns
            </button>
            {showColManager && (
              <div
                className="absolute right-0 top-full mt-1 rounded-lg shadow-xl z-40 p-3"
                style={{ background: '#fff', border: '1px solid #e5e4df', width: 200 }}
              >
                <p className="text-xs font-bold text-[#6b7280] uppercase tracking-wider mb-2">Show / Hide Columns</p>
                {DEFAULT_COLUMNS.filter(c => !c.alwaysVisible).map(col => (
                  <label key={col.key} className="flex items-center gap-2 py-1 cursor-pointer text-sm text-[#374151]">
                    <input
                      type="checkbox"
                      checked={visibleCols.includes(col.key)}
                      onChange={e => {
                        const next = e.target.checked
                          ? [...visibleCols, col.key]
                          : visibleCols.filter(k => k !== col.key);
                        updateVisibleCols(next);
                      }}
                    />
                    {col.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Import */}
          <div className="relative">
            <button
              onClick={() => { setShowImport(!showImport); setShowExport(false); setShowColManager(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border"
              style={{ borderColor: '#e5e4df', color: '#6b7280' }}
            >
              <Upload className="w-3.5 h-3.5" /> Import
            </button>
            {showImport && (
              <div
                className="absolute right-0 top-full mt-1 rounded-lg shadow-xl z-40 p-3 space-y-2"
                style={{ background: '#fff', border: '1px solid #e5e4df', width: 260 }}
              >
                <p className="text-xs font-bold text-[#6b7280] uppercase tracking-wider">Import CSV / Excel</p>
                <p className="text-xs text-[#9ca3af]">Columns: Category, Description, Supplier, Link, Image URL, Qty, Unit, Wastage, Unit Price</p>
                <input
                  ref={importRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleImportFile(f); }}
                />
                <button
                  onClick={() => importRef.current?.click()}
                  className="w-full py-2 rounded text-sm font-semibold"
                  style={{ background: '#1a1a2e', color: '#c9a84c' }}
                >
                  Select File…
                </button>
              </div>
            )}
          </div>

          {/* Export */}
          <div className="relative">
            <button
              onClick={() => { setShowExport(!showExport); setShowImport(false); setShowColManager(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border"
              style={{ borderColor: '#e5e4df', color: '#6b7280' }}
            >
              <Download className="w-3.5 h-3.5" /> Export <ChevronDown className="w-3 h-3" />
            </button>
            {showExport && (
              <div
                className="absolute right-0 top-full mt-1 rounded-lg shadow-xl z-40 overflow-hidden"
                style={{ background: '#fff', border: '1px solid #e5e4df', width: 168 }}
              >
                {[
                  { fmt: 'csv',  label: 'CSV',  icon: FileText },
                  { fmt: 'xlsx', label: 'Excel (XLSX)', icon: FileSpreadsheet },
                  { fmt: 'pdf',  label: 'PDF (A4 Landscape)', icon: FileDown },
                ].map(({ fmt: f, label, icon: Icon }) => (
                  <button
                    key={f}
                    onClick={() => f === 'pdf' ? handleExportPdf() : handleExportServer(f)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-[#f3f2ef] transition-colors"
                  >
                    <Icon className="w-4 h-4" style={{ color: '#c9a84c' }} />
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Fullscreen toggle */}
          {!isFullscreen && (
            <button
              onClick={() => setIsFullscreen(true)}
              title="Expand to full screen (2K optimised)"
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border font-semibold"
              style={{ borderColor: '#1a1a2e', color: '#1a1a2e' }}
            >
              <Maximize2 className="w-3.5 h-3.5" /> Full Screen
            </button>
          )}

          {/* Add row */}
          <button
            onClick={addRow}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded font-semibold"
            style={{ background: '#c9a84c', color: '#1a1a2e' }}
          >
            <Plus className="w-3.5 h-3.5" /> Add Item
          </button>
        </div>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────────── */}
      <div
        className="overflow-auto rounded-lg"
        style={{
          border: '1px solid #e5e4df',
          maxHeight: isFullscreen ? 'calc(100vh - 230px)' : 'calc(100vh - 340px)',
          flex: isFullscreen ? '1 1 0' : undefined,
        }}
      >
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>

          {/* Colgroup */}
          <colgroup>
            {visibleColDefs.map(c => <col key={c.key} style={{ width: c.width }} />)}
            <col style={{ width: 60 }} /> {/* actions: insert + delete */}
          </colgroup>

          {/* Header */}
          <thead>
            <tr style={{ background: '#1a1a2e', position: 'sticky', top: 0, zIndex: 10 }}>
              {visibleColDefs.map(col => (
                <th
                  key={col.key}
                  className="px-2 py-2.5 text-left"
                  style={{
                    color: '#c9a84c',
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                    borderBottom: '2px solid #c9a84c',
                  }}
                >
                  {col.key === 'selected' ? (
                    <button onClick={toggleAll} className="flex items-center justify-center w-full">
                      {items.every(it => it.selected)
                        ? <CheckSquare className="w-3.5 h-3.5 text-[#c9a84c]" />
                        : <Square className="w-3.5 h-3.5 text-[#6b7280]" />
                      }
                    </button>
                  ) : col.key === 'category' ? (
                    <button
                      onClick={cycleSortCategory}
                      className="flex items-center gap-1 group"
                      title="Sort by category"
                    >
                      Category
                      <SortIcon
                        className="w-3 h-3"
                        style={{ color: sortCategory ? '#c9a84c' : '#6b7280', opacity: sortCategory ? 1 : 0.6 }}
                      />
                    </button>
                  ) : col.label}
                </th>
              ))}
              <th style={{ background: '#1a1a2e', borderBottom: '2px solid #c9a84c', width: 60 }}></th>
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {displayItems.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColDefs.length + 1}
                  className="text-center py-16 text-sm"
                  style={{ color: '#9ca3af' }}
                >
                  No items yet.{' '}
                  <button onClick={addRow} className="underline" style={{ color: '#c9a84c' }}>Add the first item</button>
                  {' '}or use Import to load from CSV/Excel.
                </td>
              </tr>
            ) : displayItems.map((item, idx) => (
              <ItemRow
                key={item.id}
                item={item}
                idx={idx}
                visibleColDefs={visibleColDefs}
                editCell={editCell}
                setEditCell={setEditCell}
                setField={setField}
                toggleSelected={toggleSelected}
                onOpenImageModal={() => setImageModal(item.id)}
                onInsertAfter={() => insertRowAfter(item.id)}
                onDelete={() => deleteRow(item.id)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Footer totals ──────────────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-4 py-3 mt-2 rounded-lg"
        style={{ background: '#1a1a2e' }}
      >
        <span className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
          {items.length} items · {selectedItems.length} selected
          {(filterText || filterCategory || filterSupplier) && ` · ${displayItems.length} shown`}
        </span>
        <div className="flex items-center gap-6 text-sm">
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>
            Selected excl. GST: <span className="font-mono font-semibold text-white">{fmtCurrency(selectedTotal)}</span>
          </span>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>
            GST (10%): <span className="font-mono text-white">{fmtCurrency(gst)}</span>
          </span>
          <span style={{ color: '#c9a84c' }}>
            Total incl. GST: <span className="font-mono font-bold text-lg">{fmtCurrency(totalWithGst)}</span>
          </span>
        </div>
      </div>

      {/* ── Image upload modal ────────────────────────────────────────────────── */}
      {imageModal && (
        <ItemImageUpload
          currentUrl={items.find(it => it.id === imageModal)?.image_url || ''}
          onUpload={(url) => setField(imageModal, 'image_url', url)}
          onClose={() => setImageModal(null)}
        />
      )}

      {/* ── Duplicate import dialog ───────────────────────────────────────────── */}
      {pendingImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="rounded-xl shadow-2xl p-6 w-80" style={{ background: '#fff', border: '1px solid #e5e4df' }}>
            <h3 className="text-base font-bold text-[#1a1a2e] mb-2">Duplicate Items Detected</h3>
            <p className="text-sm text-[#6b7280] mb-4">
              <span className="font-semibold text-[#dc2626]">{pendingImport.dupeCount}</span> row{pendingImport.dupeCount > 1 ? 's' : ''} match existing items by description.
              <br />{pendingImport.unique.length} row{pendingImport.unique.length !== 1 ? 's' : ''} are new.
            </p>
            <div className="flex flex-col gap-2">
              <button
                className="w-full py-2 rounded text-sm font-semibold"
                style={{ background: '#1a1a2e', color: '#c9a84c' }}
                onClick={() => {
                  updateItems(prev => [...prev, ...pendingImport.unique]);
                  setPendingImport(null);
                }}
              >
                Skip duplicates — add {pendingImport.unique.length} unique row{pendingImport.unique.length !== 1 ? 's' : ''}
              </button>
              <button
                className="w-full py-2 rounded text-sm font-semibold border"
                style={{ borderColor: '#e5e4df', color: '#374151' }}
                onClick={() => {
                  updateItems(prev => [...prev, ...pendingImport.all]);
                  setPendingImport(null);
                }}
              >
                Import all — add {pendingImport.all.length} row{pendingImport.all.length !== 1 ? 's' : ''}
              </button>
              <button
                className="w-full py-2 rounded text-sm text-[#9ca3af]"
                onClick={() => setPendingImport(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Item Row ─────────────────────────────────────────────────────────────────
function ItemRow({ item, idx, visibleColDefs, editCell, setEditCell, setField, toggleSelected, onOpenImageModal, onInsertAfter, onDelete }) {
  const isEditing = (col) => editCell?.rowId === item.id && editCell?.col === col;
  const startEdit = (col) => setEditCell({ rowId: item.id, col });
  const stopEdit = () => setEditCell(null);

  const rowBg = idx % 2 === 0 ? '#f8f7f4' : '#ffffff';
  const dimmed = !item.selected;

  return (
    <tr
      style={{
        background: rowBg,
        opacity: dimmed ? 0.55 : 1,
        borderBottom: '1px solid #f0efe9',
      }}
    >
      {visibleColDefs.map(col => (
        <td
          key={col.key}
          className="px-2 py-1"
          style={{ verticalAlign: 'middle', fontSize: 12 }}
        >
          <CellContent
            col={col.key}
            item={item}
            isEditing={isEditing(col.key)}
            onStartEdit={() => startEdit(col.key)}
            onStopEdit={stopEdit}
            setField={setField}
            toggleSelected={toggleSelected}
            onOpenImageModal={onOpenImageModal}
          />
        </td>
      ))}
      {/* Actions: insert + delete */}
      <td className="px-1 py-1 text-center" style={{ verticalAlign: 'middle' }}>
        <div className="flex items-center justify-center gap-1">
          <button
            onClick={onInsertAfter}
            title="Insert row below"
            className="p-1 rounded transition-all"
            style={{ color: '#16a34a', background: '#dcfce7' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#bbf7d0'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#dcfce7'; }}
          >
            <Plus className="w-3 h-3" />
          </button>
          <button
            onClick={onDelete}
            title="Delete row"
            className="p-1 rounded transition-all"
            style={{ color: '#ef4444', background: '#fee2e2' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#fecaca'; e.currentTarget.style.color = '#b91c1c'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#ef4444'; }}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Cell Content ─────────────────────────────────────────────────────────────
function CellContent({ col, item, isEditing, onStartEdit, onStopEdit, setField, toggleSelected, onOpenImageModal }) {
  const inputStyle = {
    width: '100%', padding: '2px 6px', fontSize: 12, border: '1px solid #c9a84c',
    borderRadius: 3, outline: 'none', background: '#fffef7',
  };
  // Suppress browser number-input spinners without needing a CSS class
  const numInputStyle = {
    ...inputStyle,
    textAlign: 'right',
    MozAppearance: 'textfield',
    WebkitAppearance: 'none',
    appearance: 'textfield',
  };

  switch (col) {
    case 'selected':
      return (
        <button onClick={() => toggleSelected(item.id)} className="flex items-center justify-center w-full">
          {item.selected
            ? <CheckSquare className="w-4 h-4" style={{ color: '#c9a84c' }} />
            : <Square className="w-4 h-4" style={{ color: '#d1d5db' }} />
          }
        </button>
      );

    case 'seq':
      return <span className="font-mono text-[#9ca3af]">{item.seq + 1}</span>;

    case 'image':
      return (
        <button onClick={onOpenImageModal} className="flex items-center justify-center w-12 h-10 rounded border hover:border-[#c9a84c] transition-colors overflow-hidden" style={{ borderColor: '#e5e4df' }}>
          {item.image_url
            ? <img src={item.image_url} alt="" className="w-full h-full object-cover" />
            : <ImageIcon className="w-4 h-4 text-[#d1d5db]" />
          }
        </button>
      );

    case 'category':
      return isEditing ? (
        <input
          autoFocus type="text" defaultValue={item.category}
          style={inputStyle}
          onBlur={e => { setField(item.id, 'category', e.target.value); onStopEdit(); }}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Tab') e.target.blur(); }}
        />
      ) : (
        <span
          className="block cursor-text rounded px-1 hover:bg-yellow-50 truncate"
          onClick={onStartEdit}
          style={{ minHeight: 22, color: '#374151' }}
        >
          {item.category || <span style={{ color: '#d1d5db' }}>—</span>}
        </span>
      );

    case 'description':
      return isEditing ? (
        <input
          autoFocus type="text" defaultValue={item.description}
          style={{ ...inputStyle, width: '100%' }}
          onBlur={e => { setField(item.id, 'description', e.target.value); onStopEdit(); }}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Tab') e.target.blur(); }}
        />
      ) : (
        <span
          className="block cursor-text hover:bg-yellow-50 rounded px-1"
          onClick={onStartEdit}
          style={{ minHeight: 22, color: '#1a1a2e', fontWeight: item.description ? 500 : 300 }}
        >
          {item.description || <span style={{ color: '#d1d5db', fontWeight: 300 }}>Click to add…</span>}
        </span>
      );

    case 'supplier':
      return (
        <SupplierPicker
          value={item.supplier_name}
          supplierId={item.supplier_id}
          onSelect={({ id, name }) => {
            setField(item.id, 'supplier_id', id);
            setField(item.id, 'supplier_name', name);
          }}
        />
      );

    case 'link':
      return isEditing ? (
        <input
          autoFocus type="url" defaultValue={item.link}
          style={{ ...inputStyle, width: 140 }}
          placeholder="https://…"
          onBlur={e => { setField(item.id, 'link', e.target.value); onStopEdit(); }}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Tab') e.target.blur(); }}
        />
      ) : (
        <div className="flex items-center justify-center">
          {item.link ? (
            <a href={item.link} target="_blank" rel="noreferrer" title={item.link} onClick={e => e.stopPropagation()}>
              <LinkIcon className="w-3.5 h-3.5" style={{ color: '#3b82f6' }} />
            </a>
          ) : (
            <button onClick={onStartEdit} title="Add link">
              <LinkIcon className="w-3.5 h-3.5 text-[#d1d5db] hover:text-[#3b82f6]" />
            </button>
          )}
        </div>
      );

    case 'quantity':
      return isEditing ? (
        <input
          autoFocus
          type="text"
          inputMode="decimal"
          defaultValue={item.quantity}
          style={{ ...numInputStyle, width: 64 }}
          onBlur={e => { setField(item.id, 'quantity', parseFloat(e.target.value) || 0); onStopEdit(); }}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Tab') e.target.blur(); }}
        />
      ) : (
        <span
          className="block text-right cursor-text hover:bg-yellow-50 rounded px-1 font-mono"
          onClick={onStartEdit}
          style={{ minHeight: 22, color: '#374151' }}
        >
          {item.quantity ?? 0}
        </span>
      );

    case 'unit':
      return (
        <select
          value={item.unit || 'ea'}
          onChange={e => setField(item.id, 'unit', e.target.value)}
          style={{ fontSize: 12, border: '1px solid #e5e4df', borderRadius: 3, padding: '2px 4px', background: '#fff', width: '100%' }}
        >
          {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      );

    case 'wastage':
      return isEditing ? (
        <input
          autoFocus
          type="text"
          inputMode="decimal"
          defaultValue={item.wastage ?? 1}
          style={{ ...numInputStyle, width: 64 }}
          placeholder="1.00"
          onBlur={e => {
            const val = parseFloat(e.target.value);
            setField(item.id, 'wastage', isNaN(val) || val <= 0 ? 1 : val);
            onStopEdit();
          }}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Tab') e.target.blur(); }}
        />
      ) : (
        <span
          className="block text-right cursor-text hover:bg-yellow-50 rounded px-1 font-mono"
          onClick={onStartEdit}
          style={{ minHeight: 22, color: (item.wastage ?? 1) !== 1 ? '#c9a84c' : '#374151', fontWeight: (item.wastage ?? 1) !== 1 ? 600 : 400 }}
          title="Qty × Wastage × Unit Price = Line Total"
        >
          {fmt(item.wastage ?? 1)}
        </span>
      );

    case 'unit_price':
      return isEditing ? (
        <input
          autoFocus
          type="text"
          inputMode="decimal"
          defaultValue={item.unit_price}
          style={{ ...numInputStyle, width: '100%' }}
          onBlur={e => { setField(item.id, 'unit_price', parseFloat(e.target.value) || 0); onStopEdit(); }}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Tab') e.target.blur(); }}
        />
      ) : (
        <span
          className="block text-right cursor-text hover:bg-yellow-50 rounded px-1 font-mono"
          onClick={onStartEdit}
          style={{ minHeight: 22, color: '#374151' }}
        >
          {fmtCurrency(item.unit_price)}
        </span>
      );

    case 'line_total':
      return (
        <span
          className="block text-right font-mono font-semibold px-1"
          style={{ color: item.selected ? '#1a1a2e' : '#9ca3af' }}
          title={`${item.quantity ?? 0} × ${item.wastage ?? 1} × ${fmtCurrency(item.unit_price)}`}
        >
          {fmtCurrency(item.line_total)}
        </span>
      );

    default:
      return null;
  }
}
